import md5 from 'crypto-js/md5'

import { getApiOrigin } from './api-client/getApiOrigin'
import { isPWA } from './device'

// The auth is derived from the first 7 digits of the server's 10-digit unix
// timestamp, so it only changes when the server's 1000 s window rolls over.
// Cache expiry is computed from the server timestamp itself (not blindly from
// the client clock) so a hash fetched near the end of a window is never
// served after the server has moved to the next prefix.
const WINDOW_S = 1000
const MAX_CACHE_TTL_MS = 30_000
// Covers response transit time between the server stamping the timestamp and
// the client computing the remaining window.
const SAFETY_MARGIN_MS = 5_000
// A hung fetch must not leave the shared `inflight` promise pending forever —
// every later caller would await it and all guest API requests would hang
// until reload. Abort instead so `finally` runs and the next call retries.
const FETCH_TIMEOUT_MS = 10_000

let inflight: Promise<string> | null = null
let cached: { value: string; expiresAt: number } | null = null

export const getPublicAuth = async () => {
  // The second bound detects a client clock that jumped BACKWARDS after the
  // value was cached (NTP correction, manual change): `expiresAt - now` can
  // never legitimately exceed the largest TTL we ever set, so a bigger gap
  // means the entry would otherwise stay "fresh" for the whole jump distance
  // while the server window rolls over underneath it.
  if (
    cached &&
    Date.now() < cached.expiresAt &&
    cached.expiresAt - Date.now() <= MAX_CACHE_TTL_MS
  ) {
    return cached.value
  }
  if (inflight) return inflight

  const request = (async () => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    try {
      const controller = new AbortController()
      timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
      const key = import.meta.env.VITE_API_KEY
      const res = await fetch(
        `${getApiOrigin({})}/increase.php?display_platform=${isPWA() ? '2' : '0'}`,
        { signal: controller.signal },
      )
      // Deliberately no res.ok gate — availability first: a degraded edge or
      // PHP error that still carries the timestamp body must keep producing
      // auth, exactly like the pre-cache implementation. Validate the BODY
      // instead: the hash formula (first 7 of 10 digits, 1000 s window math)
      // is only defined for 10-digit unix seconds, so anything else — HTML
      // error page (json() throws), WAF JSON, a proxy echoing "503" — throws
      // rather than being hashed and cached for a full TTL.
      const raw: unknown = await res.json()
      const serverTime = Number(raw)
      if (
        !Number.isFinite(serverTime) ||
        serverTime < 1_000_000_000 ||
        serverTime >= 10_000_000_000
      ) {
        throw new Error('increase.php returned an invalid server time')
      }
      const value = md5(
        `${serverTime.toString().substring(0, 7)}${key}`,
      ).toString()

      const windowRemainingMs =
        (WINDOW_S - (serverTime % WINDOW_S)) * 1000 - SAFETY_MARGIN_MS
      const ttlMs = Math.min(MAX_CACHE_TTL_MS, windowRemainingMs)
      // Too close to the window boundary: skip caching entirely; in-flight
      // dedup still collapses concurrent callers into this one fetch.
      cached = ttlMs > 0 ? { value, expiresAt: Date.now() + ttlMs } : null

      return value
    } finally {
      clearTimeout(timeout)
    }
  })()

  inflight = request
  // Release the slot on settlement so a failure is never served to later
  // calls — they must retry with a fresh fetch. This is attached AFTER the
  // assignment (not inside the async body): if the body throws synchronously
  // (e.g. no AbortController in an old WebView), an inner finally would run
  // BEFORE the assignment and the rejected promise would occupy `inflight`
  // forever. No successor can appear before this runs (creation requires the
  // slot to be empty), so the unconditional null is safe. The trailing catch
  // only silences the derived promise — callers still observe `request`.
  void request
    .finally(() => {
      inflight = null
    })
    .catch(() => undefined)

  return request
}
